import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  Avatar,
  IconButton,
  Chip,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  AccountBox as AccountBoxIcon,
  Book as BookIcon,
  AttachMoney as MoneyIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  CameraAlt as CameraAltIcon,
  CameraEnhance as CameraEnhanceIcon,
  FlipCameraIos as FlipCameraIosIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  generateMessage,
  refreshWhatsappConfig,
} from '../../services/whatsappTemplateService';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // In production (ondigitalocean.app or services.knls.in), use relative URL
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  // Development fallback
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();
const STUDENT_APP_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.knls.academy';

// Color palette for courses (will cycle through if more courses than colors)
const courseColors = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#ef4444', '#06b6d4', '#f97316', '#84cc16', '#a855f7',
];

const StudentRegistration = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [customFees, setCustomFees] = useState({}); // Track custom fees for each course
  const [showAddCourseDialog, setShowAddCourseDialog] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' for front camera, 'environment' for back camera
  const [newCourse, setNewCourse] = useState({
    course_name: '',
    fee: '',
    duration_days: '45',
  });
  const [formData, setFormData] = useState({
    name: '',
    father_name: '',
    date_of_birth: '',
    qualification: '',
    profession: '',
    father_profession: '',
    phone: '',
    whatsapp_number: '',
    address: '',
    city: '',
    village: '',
    district: '',
    state: '',
    pincode: '',
    enrolled_courses: [],
    paid_fee: 0,
    joining_date: new Date().toISOString().split('T')[0],
    photo: null,
  });
  const [successDialog, setSuccessDialog] = useState({ open: false, password: '', studentId: '' });
  const [acceptedTermsAdmin, setAcceptedTermsAdmin] = useState(false);

  // Fetch courses from API on component mount
  React.useEffect(() => {
    const fetchCourses = async () => {
      try {
        setCoursesLoading(true);
        // Use axios interceptor which automatically adds the correct token
        const response = await axios.get(`${API_BASE_URL}/students/courses/list`);
        
        if (response.data.success && response.data.courses) {
          // Add colors to courses dynamically
          const coursesWithColors = response.data.courses.map((course, index) => ({
            ...course,
            color: courseColors[index % courseColors.length], // Cycle through colors
          }));
          setCourses(coursesWithColors);
        }
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        toast.error('Failed to load courses. Please refresh the page.');
      } finally {
        setCoursesLoading(false);
      }
    };

    fetchCourses();
  }, []);

  // Function to add custom course
  const handleAddCustomCourse = async () => {
    if (!newCourse.course_name || !newCourse.fee || !newCourse.duration_days) {
      toast.error('Please fill all fields: Course Name, Fee, and Duration');
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/students/courses/create`, {
        course_name: newCourse.course_name,
        fee: parseFloat(newCourse.fee),
        duration_days: parseInt(newCourse.duration_days),
      });

      if (response.data.success) {
        const addedCourse = response.data.course;
        // Add color to the new course
        const courseWithColor = {
          ...addedCourse,
          color: courseColors[courses.length % courseColors.length],
        };
        
        // Add to courses list
        setCourses([...courses, courseWithColor]);
        
        // Reset form and close dialog
        setNewCourse({
          course_name: '',
          fee: '',
          duration_days: '45',
        });
        setShowAddCourseDialog(false);
        
        toast.success('Custom course added successfully!');
      }
    } catch (error) {
      console.error('Failed to add custom course:', error);
      toast.error(error.response?.data?.message || 'Failed to add custom course');
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo size should be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFormData({ ...formData, photo: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setFormData({ ...formData, photo: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Camera functions
  const startCamera = async () => {
    try {
      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please check permissions or use file upload.');
      setShowCameraDialog(false);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          // Check file size
          if (blob.size > 5 * 1024 * 1024) {
            toast.error('Captured photo is too large. Please try again.');
            return;
          }

          // Create a File object from blob
          const file = new File([blob], 'captured-photo.jpg', { type: 'image/jpeg' });
          
          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setPhotoPreview(reader.result);
            setFormData({ ...formData, photo: file });
          };
          reader.readAsDataURL(file);

          // Stop camera and close dialog
          stopCamera();
          setShowCameraDialog(false);
          toast.success('Photo captured successfully!');
        }
      }, 'image/jpeg', 0.9); // 90% quality
    }
  };

  const switchCamera = () => {
    // Stop current stream first
    stopCamera();
    
    // Update facing mode - useEffect will handle restarting camera
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
  };

  const handleOpenCamera = () => {
    setShowCameraDialog(true);
  };

  const handleCloseCamera = () => {
    stopCamera();
    setShowCameraDialog(false);
  };

  // Start camera when dialog opens or facing mode changes
  useEffect(() => {
    if (showCameraDialog) {
      startCamera();
    } else {
      stopCamera();
    }

    // Cleanup on unmount
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCameraDialog, facingMode]);

  const handleCourseChange = (courseCode, checked) => {
    setFormData((prev) => {
      // Prevent duplicate entries - use Set to ensure uniqueness
      const currentCourses = prev.enrolled_courses;
      const alreadyExists = currentCourses.includes(courseCode);
      
      if (checked) {
        // Only add if it doesn't already exist
        if (alreadyExists) {
          // Course already in list, don't add again
          return prev;
        }
        // Add course using Set to ensure no duplicates
        const uniqueCourses = [...new Set([...currentCourses, courseCode])];
        return {
          ...prev,
          enrolled_courses: uniqueCourses,
        };
      } else {
        // Remove course
        return {
          ...prev,
          enrolled_courses: currentCourses.filter((c) => c !== courseCode),
        };
      }
    });
    // Remove custom fee when course is deselected
    if (!checked) {
      setCustomFees((prev) => {
        const newFees = { ...prev };
        delete newFees[courseCode];
        return newFees;
      });
    }
  };

  const handleFeeChange = (courseCode, fee) => {
    const feeValue = parseFloat(fee) || 0;
    setCustomFees((prev) => ({
      ...prev,
      [courseCode]: feeValue > 0 ? feeValue : undefined,
    }));
  };

  const getCourseFee = (courseCode) => {
    // Return custom fee if set, otherwise return default fee from course
    if (customFees[courseCode] !== undefined) {
      return customFees[courseCode];
    }
    const course = courses.find((c) => c.course_code === courseCode);
    return parseFloat(course?.fee) || 0;
  };

  const calculateTotalFee = () => {
    if (!courses || courses.length === 0) return 0;
    return formData.enrolled_courses.reduce((total, code) => {
      return total + getCourseFee(code);
    }, 0);
  };

  const calculateDueFee = () => {
    return Math.max(0, calculateTotalFee() - formData.paid_fee);
  };

  const sendWhatsAppMessage = async (student, basePassword, dateOfBirth) => {
    try {
      // Generate auto-login link
      const baseUrl = window.location.origin;
      const loginUrl = `${baseUrl}/student/login?phone=${student.phone}&auto=true`;
      
      // Backend returns full password format: last 4 digits of phone + birth year (YYYY).
      // Use it directly to avoid appending year twice.
      let fullPassword = (basePassword || '').toString().trim();

      // Fallback only if backend password is missing.
      if (!fullPassword) {
        const last4 = (student.phone || '').replace(/\D/g, '').slice(-4);
        if (dateOfBirth) {
          const birthYear = new Date(dateOfBirth).getFullYear();
          fullPassword = `${last4}${birthYear}`;
        } else {
          fullPassword = `${last4}${new Date().getFullYear()}`;
        }
      }
      
      const fullLoginUrl = `${loginUrl}&password=${fullPassword}`;
      
      await refreshWhatsappConfig();
      const message = generateMessage('studentRegistration', {
        studentName: student.name,
        studentId: student.student_id || student.id,
        phone: student.phone,
        password: fullPassword,
        autoLoginUrl: fullLoginUrl,
        studentLoginUrl: `${baseUrl}/student/login`,
        studentAppPlayStoreUrl: STUDENT_APP_PLAY_STORE_URL,
      });
      
      // Use parent WhatsApp if provided, otherwise fall back to the student phone.
      const whatsappNumber = formData.whatsapp_number || formData.phone;
      const cleanWhatsAppNumber = whatsappNumber.replace(/\D/g, '');
      
      if (cleanWhatsAppNumber.length !== 10) {
        toast.error('Invalid WhatsApp number');
        return;
      }
      
      // Open WhatsApp with the message
      const whatsappUrl = `https://wa.me/91${cleanWhatsAppNumber}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Opening WhatsApp with login credentials...');
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
      toast.error('Failed to open WhatsApp');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }

    if (formData.enrolled_courses.length === 0) {
      toast.error('Please select at least one course');
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error('Phone number must be 10 digits');
      return;
    }

    if (!acceptedTermsAdmin) {
      toast.error('You must accept Terms & Conditions and confirm fees are non-refundable before registering');
      return;
    }

    setLoading(true);

    try {
      // Prepare course fees data (custom fees override default fees)
      const courseFees = formData.enrolled_courses.reduce((acc, courseCode) => {
        acc[courseCode] = getCourseFee(courseCode);
        return acc;
      }, {});

      const payload = {
        name: formData.name,
        father_name: formData.father_name || undefined,
        date_of_birth: formData.date_of_birth || undefined,
        qualification: formData.qualification || undefined,
        profession: formData.profession || undefined,
        father_profession: formData.father_profession || undefined,
        phone: formData.phone,
        whatsapp_number: formData.whatsapp_number || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        village: formData.village || undefined,
        district: formData.district || undefined,
        state: formData.state || undefined,
        pincode: formData.pincode || undefined,
        enrolled_courses: formData.enrolled_courses,
        course_fees: courseFees, // Include custom fees
        paid_fee: formData.paid_fee,
        joining_date: formData.joining_date,
        photo_url: photoPreview || undefined, // Send base64 photo
        accepted_terms: true,
      };

      // Use axios interceptor which automatically adds the correct token
      const response = await axios.post(`${API_BASE_URL}/students`, payload);

      if (response.data.success) {
        const studentData = response.data.student;
        const basePassword = response.data.password; // Full password: last4 + YYYY
        
        setSuccessDialog({
          open: true,
          password: basePassword || 'N/A',
          studentId: studentData?.student_id || 'N/A',
        });

        // Automatically send WhatsApp message with login credentials
        if (studentData && (formData.whatsapp_number || formData.phone)) {
          // Small delay to ensure dialog opens first
          setTimeout(() => {
            sendWhatsAppMessage(
              {
                ...studentData,
                phone: formData.phone,
              },
              basePassword,
              formData.date_of_birth
            );
          }, 500);
        }
      }
    } catch (error) {
      console.error('❌ Error registering student:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        request: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data,
        },
      });
      
      let errorMessage = error.response?.data?.message || error.message || 'Failed to register student';
      const errorDetails = error.response?.data?.details;
      
      // Add details to error message if available
      if (errorDetails) {
        if (errorDetails.detail) {
          errorMessage += `\nDetail: ${errorDetails.detail}`;
        }
        if (errorDetails.hint) {
          errorMessage += `\nHint: ${errorDetails.hint}`;
        }
      }
      
      // Show error with longer duration for detailed messages
      toast.error(errorMessage, { 
        autoClose: errorDetails ? 8000 : 5000,
        style: { whiteSpace: 'pre-line' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog({ open: false, password: '' });
    navigate('/dashboard/students');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          p: 3,
          mb: 3,
          color: 'white',
          boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <PersonIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Student Registration
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Register new students for courses
            </Typography>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Left Column - Personal Information */}
          <Grid item xs={12} md={8}>
            {/* Personal Details Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <AccountBoxIcon sx={{ color: '#667eea', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Personal Information
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Student Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Father's Name"
                      value={formData.father_name}
                      onChange={(e) => setFormData({ ...formData, father_name: e.target.value })}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Date of Birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Qualification"
                      value={formData.qualification}
                      onChange={(e) => setFormData({ ...formData, qualification: e.target.value })}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SchoolIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Student Profession"
                      value={formData.profession}
                      onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                      fullWidth
                      placeholder="e.g., Student, Employee, Business"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Father Profession"
                      value={formData.father_profession}
                      onChange={(e) => setFormData({ ...formData, father_profession: e.target.value })}
                      fullWidth
                      placeholder="e.g., Farmer, Employee, Business"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                      required
                      fullWidth
                      inputProps={{ maxLength: 10 }}
                      helperText="10-digit mobile number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Parent WhatsApp Number"
                      value={formData.whatsapp_number}
                      onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value.replace(/\D/g, '') })}
                      fullWidth
                      inputProps={{ maxLength: 10 }}
                      helperText="10-digit parent WhatsApp number (optional)"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  {/* Address Fields */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
                      Address Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Full Address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="House/Building number, Street, Area"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="City / Village"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Street"
                      value={formData.village}
                      onChange={(e) => setFormData({ ...formData, village: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="District"
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="State"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="PIN Code"
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value.replace(/\D/g, '') })}
                      fullWidth
                      inputProps={{ maxLength: 6 }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Courses Selection Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BookIcon sx={{ color: '#8b5cf6', fontSize: 28 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Enrolled Courses *
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowAddCourseDialog(true)}
                    sx={{
                      borderRadius: 2,
                      borderColor: '#8b5cf6',
                      color: '#8b5cf6',
                      '&:hover': {
                        borderColor: '#7c3aed',
                        bgcolor: '#8b5cf610',
                      },
                    }}
                  >
                    Add Custom Course
                  </Button>
                </Box>

                {coursesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : courses.length === 0 ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    No courses available. Please add courses in the system first.
                  </Alert>
                ) : (
                  <Grid container spacing={2}>
                    {courses.map((course) => (
                      <Grid item xs={12} sm={6} key={course.course_code}>
                        <Paper
                          elevation={formData.enrolled_courses.includes(course.course_code) ? 4 : 1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            cursor: 'pointer',
                            border: formData.enrolled_courses.includes(course.course_code)
                              ? `2px solid ${course.color}`
                              : '2px solid transparent',
                            bgcolor: formData.enrolled_courses.includes(course.course_code)
                              ? `${course.color}10`
                              : 'background.paper',
                            transition: 'all 0.3s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: 4,
                            },
                          }}
                          onClick={() => handleCourseChange(course.course_code, !formData.enrolled_courses.includes(course.course_code))}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Checkbox
                              checked={formData.enrolled_courses.includes(course.course_code)}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent Paper onClick from firing
                                handleCourseChange(course.course_code, e.target.checked);
                              }}
                              onClick={(e) => e.stopPropagation()} // Also stop propagation on click
                              sx={{
                                color: course.color,
                                '&.Mui-checked': {
                                  color: course.color,
                                },
                              }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {course.course_name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {course.duration_days || 45} days
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', minWidth: '40px' }}>
                                    Fee:
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={getCourseFee(course.course_code)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const newFee = e.target.value;
                                      handleFeeChange(course.course_code, newFee);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={(e) => {
                                      e.stopPropagation();
                                      e.target.select(); // Select all text when focused
                                    }}
                                    placeholder={course.fee ? parseFloat(course.fee).toLocaleString() : '0'}
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: course.color }}>₹</Typography>
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      width: '140px',
                                      '& .MuiOutlinedInput-root': {
                                        height: '38px',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        bgcolor: formData.enrolled_courses.includes(course.course_code) 
                                          ? `${course.color}12` 
                                          : 'background.paper',
                                        border: `2px solid ${formData.enrolled_courses.includes(course.course_code) ? course.color : '#d0d0d0'}`,
                                        borderRadius: '8px',
                                        '&:hover': {
                                          borderColor: course.color,
                                          bgcolor: formData.enrolled_courses.includes(course.course_code) 
                                            ? `${course.color}18` 
                                            : 'background.paper',
                                        },
                                        '&.Mui-focused': {
                                          borderColor: course.color,
                                          borderWidth: '2px',
                                          bgcolor: `${course.color}15`,
                                          boxShadow: `0 0 0 3px ${course.color}20`,
                                        },
                                        '& input': {
                                          padding: '8px 12px',
                                          fontWeight: '600',
                                          color: formData.enrolled_courses.includes(course.course_code) ? course.color : 'inherit',
                                        },
                                      },
                                    }}
                                    inputProps={{
                                      min: 0,
                                      step: 1,
                                    }}
                                  />
                                  {customFees[course.course_code] !== undefined && 
                                   course.fee && customFees[course.course_code] !== parseFloat(course.fee) && (
                                    <Chip
                                      icon={<MoneyIcon sx={{ fontSize: '14px !important' }} />}
                                      label={`Default: ₹${course.fee ? parseFloat(course.fee).toLocaleString() : '0'}`}
                                      size="small"
                                      sx={{
                                        height: '26px',
                                        fontSize: '0.7rem',
                                        bgcolor: 'rgba(255, 152, 0, 0.1)',
                                        color: '#f57c00',
                                        border: '1px solid rgba(255, 152, 0, 0.3)',
                                        fontWeight: 'medium',
                                        '& .MuiChip-icon': {
                                          color: '#f57c00',
                                        },
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            {formData.enrolled_courses.includes(course.course_code) && (
                              <CheckCircleIcon sx={{ color: course.color }} />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Joining Date */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.05)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarIcon sx={{ color: '#10b981', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Enrollment Details
                  </Typography>
                </Box>
                <TextField
                  label="Joining Date"
                  type="date"
                  value={formData.joining_date}
                  onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
                <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Duration:</strong> 45 days (default for all courses)
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Photo & Fee Summary */}
          <Grid item xs={12} md={4}>
            {/* Photo Upload Card */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.05)',
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
                  <PhotoCameraIcon sx={{ fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Student Photo
                  </Typography>
                </Box>

                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <Avatar
                    src={photoPreview}
                    sx={{
                      width: 120,
                      height: 120,
                      border: '4px solid white',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                      bgcolor: 'rgba(255,255,255,0.2)',
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  {photoPreview && (
                    <IconButton
                      onClick={handleRemovePhoto}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                      }}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                      borderRadius: 2,
                    }}
                    fullWidth
                  >
                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={<CameraAltIcon />}
                    onClick={handleOpenCamera}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                      borderRadius: 2,
                    }}
                    fullWidth
                  >
                    Capture from Camera
                  </Button>
                </Box>
                
                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.9 }}>
                  Max size: 5MB (JPG, PNG)
                </Typography>
              </CardContent>
            </Card>

            {/* Fee Summary Card */}
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.05)',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <MoneyIcon sx={{ fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Fee Summary
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Selected Courses */}
                  {formData.enrolled_courses.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, opacity: 0.9 }}>
                        Selected Courses:
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Remove duplicates and map courses */}
                        {[...new Set(formData.enrolled_courses)].map((courseCode, index) => {
                          const course = courses.find((c) => c.course_code === courseCode);
                          if (!course) return null;
                          const courseFee = getCourseFee(courseCode);
                          return (
                            <Paper
                              key={`${courseCode}-${index}`}
                              sx={{
                                p: 1.5,
                                bgcolor: 'rgba(255, 255, 255, 0.15)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: 2,
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {course.course_name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {course.duration_days || 45} days
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    ₹{courseFee.toLocaleString()}
                                  </Typography>
                                  {customFees[courseCode] !== undefined && 
                                   course.fee && customFees[courseCode] !== parseFloat(course.fee) && (
                                    <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem', display: 'block' }}>
                                      (edited)
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                  {/* Total Summary */}
                  <Box
                    sx={{
                      pt: 2,
                      borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Total Fee:
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        ₹{calculateTotalFee().toLocaleString()}
                      </Typography>
                    </Box>

                    <TextField
                      label="Paid Fee"
                      type="number"
                      value={formData.paid_fee}
                      onChange={(e) =>
                        setFormData({ ...formData, paid_fee: parseFloat(e.target.value) || 0 })
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ color: 'white' }}>₹</Typography>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-root': {
                          color: 'white',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.7)',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.8)',
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: 'white',
                        },
                      }}
                      fullWidth
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Due:
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight="bold"
                        sx={{
                          color: calculateDueFee() > 0 ? '#ffeb3b' : '#4caf50',
                        }}
                      >
                        ₹{calculateDueFee().toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <FormControlLabel
          sx={{ alignItems: 'flex-start', mt: 3, mr: 0, display: 'flex' }}
          control={
            <Checkbox
              checked={acceptedTermsAdmin}
              onChange={(e) => setAcceptedTermsAdmin(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I agree to Terms & Conditions and understand that fees are non-refundable.{' '}
              <RouterLink to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                View terms
              </RouterLink>
            </Typography>
          }
        />

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end',
            mt: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/dashboard/students')}
            startIcon={<CancelIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              borderColor: '#e0e0e0',
              '&:hover': {
                borderColor: '#bdbdbd',
                bgcolor: '#f5f5f5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)',
              },
            }}
          >
            {loading ? 'Registering...' : 'Register Student'}
          </Button>
        </Box>
      </form>

      {/* Add Custom Course Dialog */}
      <Dialog
        open={showAddCourseDialog}
        onClose={() => {
          setShowAddCourseDialog(false);
          setNewCourse({ course_name: '', fee: '', duration_days: '45' });
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ color: '#8b5cf6', fontSize: 28 }} />
            <Typography variant="h5" fontWeight="bold">
              Add Custom Course
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Course Name *"
              value={newCourse.course_name}
              onChange={(e) => setNewCourse({ ...newCourse, course_name: e.target.value })}
              fullWidth
              placeholder="e.g., Advanced Excel, Python Programming"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Duration (Days) *"
                type="number"
                value={newCourse.duration_days}
                onChange={(e) => setNewCourse({ ...newCourse, duration_days: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                label="Fee (₹) *"
                type="number"
                value={newCourse.fee}
                onChange={(e) => setNewCourse({ ...newCourse, fee: e.target.value })}
                fullWidth
                required
                inputProps={{ min: 0, step: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                This course will be added to the system and will appear in the enrolled courses list for future registrations.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setShowAddCourseDialog(false);
              setNewCourse({ course_name: '', fee: '', duration_days: '45' });
            }}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddCustomCourse}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              },
            }}
          >
            Add Course
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Dialog */}
      <Dialog
        open={showCameraDialog}
        onClose={handleCloseCamera}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CameraEnhanceIcon sx={{ color: '#667eea', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                Capture Photo
              </Typography>
            </Box>
            {!isMobile && (
              <IconButton
                onClick={switchCamera}
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.2)',
                  },
                }}
              >
                <FlipCameraIosIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ position: 'relative', width: '100%', bgcolor: '#000', borderRadius: 2, overflow: 'hidden' }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                maxHeight: '500px',
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {!stream && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.7)',
                }}
              >
                <CircularProgress sx={{ color: 'white' }} />
              </Box>
            )}
          </Box>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
            <Typography variant="body2">
              Position yourself in the frame and click "Capture Photo" when ready.
              {!isMobile && ' Use the flip icon to switch between front and back camera.'}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleCloseCamera}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={capturePhoto}
            variant="contained"
            startIcon={<CameraAltIcon />}
            disabled={!stream}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            Capture Photo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successDialog.open}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">
              Student Registered Successfully!
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Student ID - Most Important */}
            <Box
              sx={{
                p: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
              }}
            >
              <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
                Student ID (For Certificates & Verification)
              </Typography>
              <Typography variant="h3" fontWeight="bold" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                {successDialog.studentId}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1, display: 'block' }}>
                This ID will be used for certificate issuance and public verification
              </Typography>
            </Box>

            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="body1">
                <strong>Student Name:</strong> {formData.name}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                <strong>Phone:</strong> {formData.phone}
              </Typography>
            </Paper>
            <Box
              sx={{
                p: 2,
                bgcolor: '#e3f2fd',
                borderRadius: 2,
                border: '2px solid #2196f3',
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Default Password:
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold" sx={{ textAlign: 'center', py: 1 }}>
                {successDialog.password}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Please share this password with the student.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentRegistration;
